import React, { Children, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ColumnSelect } from './table-columns'

// A list's select mode (owner 2026-10-29: "a select mode so we can select
// all but then uncheck a couple and perform an action on all the selected
// things"; "actions and selecting rows and select all deselect all etc
// should be common things shared"). Nothing here knows what the rows are:
// the page says how a row is named and what its selection is sent as.
//
// A selection is one of two things:
//   ids       the rows ticked one by one (explicit ids);
//   matching  every row the list's filters match, except the ones unticked
//             since (all matching except these ids). Rows an infinite grid
//             has not loaded yet are in it too.
//
// useGridSelection({ total, max, matching, idOf, labelOf, resetKey }) ->
// selection, for FleetGrid's `selection` and the grids' (TableAgInfinite,
// TableAgClient):
//   total     how many rows the filters match, when the page knows it
//             (null otherwise): the count of "all matching except 2".
//   max       the most rows the ids form holds (the Errors page's bulk
//             actions take 200); a tick past it is refused.
//   matching  false for a page whose server takes ids only: the header box
//             then ticks the rows the grid has loaded, up to max.
//   idOf      a row's id (row.id by default); labelOf(row) names a row's box
//             for assistive tech ("Select session a41c09f2").
//   resetKey  the page's filters (any value): a change clears the selection.
//             The grids clear it too when their search or reloadKey changes.
//
// selection:
//   selecting, start(), done()    select mode itself (the checkbox column,
//                                 the menu's selection actions);
//   isSelected(id), toggle(id), selectAll(loadedIds), selectAllMatching(),
//   deselectAll();
//   count     how many are selected; null for "all matching" of an unknown
//             total;
//   none, all, header ('none' | 'some' | 'all'): what the header box shows;
//   mode ('ids' | 'matching'), ids (ticked, or excepted), since (when "all
//   matching" was chosen, an ISO time: the page's snapshot, if it wants one);
//   request(filter) -> { ids } or { filter, except? }: what the page sends.
const EMPTY = { selecting: false, mode: 'ids', keys: new Set(), values: new Map(), since: null }

const keyOf = id => String(id)
const defaultIdOf = row => (row ? row.id : undefined)

const without = (state, key) => {
  const keys = new Set(state.keys)
  const values = new Map(state.values)
  keys.delete(key)
  values.delete(key)
  return { ...state, keys, values }
}
const withId = (state, key, id) => {
  const keys = new Set(state.keys)
  const values = new Map(state.values)
  keys.add(key)
  values.set(key, id)
  return { ...state, keys, values }
}
const cleared = state => ({ ...state, mode: 'ids', keys: new Set(), values: new Map(), since: null })

export const useGridSelection = ({ total = null, max = Infinity, matching = true, idOf = defaultIdOf, labelOf, resetKey } = {}) => {
  const [state, setState] = useState(EMPTY)
  const known = Number.isFinite(total)

  // the page's filters changed: what was selected was another list's
  const lastReset = useRef(resetKey)
  useEffect(() => {
    if (lastReset.current === resetKey) return
    lastReset.current = resetKey
    setState(cleared)
  }, [resetKey])

  const start = useCallback(() => setState(s => ({ ...cleared(s), selecting: true })), [])
  const done = useCallback(() => setState(EMPTY), [])
  const deselectAll = useCallback(() => setState(s => (s.mode === 'ids' && !s.keys.size ? s : cleared(s))), [])
  const selectAllMatching = useCallback(() => {
    if (!matching) return
    setState(s => ({ ...cleared(s), mode: 'matching', since: new Date().toISOString() }))
  }, [matching])
  const selectAll = useCallback(loadedIds => {
    if (matching) {
      setState(s => ({ ...cleared(s), mode: 'matching', since: new Date().toISOString() }))
      return
    }
    setState(s => (loadedIds || []).slice(0, max).reduce((next, id) => withId(next, keyOf(id), id), cleared(s)))
  }, [matching, max])
  // In the ids form a tick adds the row; in the matching form it is an
  // untick (or a re-tick) of one row of all matching.
  const toggle = useCallback(id => setState(s => {
    const key = keyOf(id)
    if (s.keys.has(key)) return without(s, key)
    if (s.mode === 'ids' && s.keys.size >= max) return s
    return withId(s, key, id)
  }), [max])

  return useMemo(() => {
    const isMatching = state.mode === 'matching'
    const size = state.keys.size
    const count = isMatching ? (known ? Math.max(0, total - size) : null) : size
    const none = isMatching ? count === 0 : size === 0
    // every row: all matching with none unticked, or every one of a known total ticked
    const all = isMatching ? size === 0 && !none : known && total > 0 && size >= total
    const ids = [...state.values.values()]
    return {
      selecting: state.selecting,
      start,
      done,
      mode: state.mode,
      canMatch: matching,
      ids,
      since: state.since,
      total: known ? total : null,
      count,
      none,
      all,
      header: none ? 'none' : all ? 'all' : 'some',
      atMax: !isMatching && size >= max,
      max,
      idOf,
      labelOf,
      isSelected: id => (isMatching ? !state.keys.has(keyOf(id)) : state.keys.has(keyOf(id))),
      toggle,
      selectAll,
      selectAllMatching,
      deselectAll,
      request: filter => (isMatching ? { filter, ...(ids.length ? { except: ids } : {}) } : { ids })
    }
  }, [state, known, total, matching, max, idOf, labelOf, start, done, toggle, selectAll, selectAllMatching, deselectAll])
}

// How many are selected, in words: "12 selected", "All matching selected",
// "All matching except 2 selected" (an unknown total).
export const selectedText = selection => {
  if (!selection) return ''
  if (selection.count !== null) return `${selection.count.toLocaleString()} selected`
  return selection.ids.length ? `All matching except ${selection.ids.length.toLocaleString()} selected` : 'All matching selected'
}

// The grids' side (TableAgInfinite, TableAgClient): the page's columns with
// the checkbox column first while the selection is selecting, and a clear
// that reads the latest selection (the grids call it from their own
// handlers and effects).
export const useSelectColumn = (children, selection) => {
  const selecting = !!(selection && selection.selecting)
  const columnChildren = useMemo(
    () => (selecting ? [<ColumnSelect key="tb-select" selection={selection} />, ...Children.toArray(children)] : children),
    [selecting, selection, children]
  )
  const latest = useRef(selection)
  latest.current = selection
  const clearSelection = useCallback(() => {
    if (latest.current) latest.current.deselectAll()
  }, [])
  return { columnChildren, clearSelection }
}

// The ids a selection holds among rows a page has in full (a client grid's):
// for a server that takes ids only, "all matching except" is resolved here.
export const selectedIdsOf = (selection, rows) => {
  if (!selection) return []
  if (selection.mode === 'ids') return selection.ids
  return (rows || []).map(row => selection.idOf(row)).filter(id => selection.isSelected(id))
}
